import { LinkProps, ImageProps, MetaProps } from '@repo/ui/src/types';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import { appConfig } from '../../utils/appConfig';
import LinkGroup, { LinkGroupProps, LinkWithIconProps } from './_LinkGroup';
import LinkList, { LinkListProps } from './_LinkList';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import ProductList from './_ProductList';
import dynamic from 'next/dynamic';
import NavigationPromo from './_NavigationPromo';
import RightContent from './_RightContent';
import { classNames } from '@repo/ui/src/utils/classNames';
const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface NavDropdownProps {
  _meta: MetaProps;
  title?: string;
  description?: string;
  primaryHeader?: string;
  secondaryHeader?: string;
  navigationPromo?: {
    links: Array<LinkProps>;
    title: string;
    image?: ImageProps;
  };
  centerContent?: Array<LinkGroupProps | LinkListProps>;
  rightContent: {
    title: string;
    links: Array<LinkProps>;
    socialLinks?: {
      title?: string;
      links?: Array<LinkWithIconProps>;
    };
    search: {
      searchBy: string;
      searchPlaceholder: string;
    };
  };
  setActiveDropdown?: StateSetterFunction<NavDropdownProps | null>;
  showMobileHeader?: boolean;
}

interface Props extends NavDropdownProps {
  searchLink?: LinkProps;
  SocialLinks?: React.ComponentType<{ socialLinks: any }>;
}
const componentTypes = {
  'linkGroup.json': LinkGroup,
  'linkList.json': LinkList,
  'productList.json': ProductList,
};

const NavDropdown = ({
  SocialLinks,
  centerContent,
  rightContent,
  description,
  title,
  searchLink,
  navigationPromo,
  setActiveDropdown,
  primaryHeader,
  secondaryHeader,
  showMobileHeader = false,
}: Props) => {
  return (
    <>
      {showMobileHeader && (
        <div className="sm:tw-hidden tw-py-2 tw-relative tw-text-black tw-border-b tw-border-b-warm-200 tw-mb-5">
          <button
            className="tw-absolute tw-left-0 tw-p-3 tw-flex"
            aria-label="back previous navigation menu"
            onClick={() => setActiveDropdown?.(null)}
          >
            <ChevronIcon direction="left" className="!tw-w-6 !tw-h-6" />
          </button>
          <h2 className="tra-text-web-heading-4 tw-text-black tw-text-center tw-py-2">{title}</h2>
        </div>
      )}
      <div className="tw-relative tw-gap-y-10 tw-grid tw-content-start sm:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:tw-grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,2fr)] sm:tw-pt-10 md:tw-pb-15 sm:tw-px-6 md:tw-px-10 lg:tw-px-20 tw-w-full tw-h-full tw-gap-7 -tw-z-100 tw-max-w-[1680px] tw-pb-6 sm:tw-max-h-[calc(85vh-var(--tra-nav-height)-var(--dropdown-offset))] sm:tw-overflow-y-auto">
        <div className="tw-hidden lg:tw-flex tw-flex-col tw-max-w-[240px] tw-gap-6">
          <h4
            className={classNames(
              'tw-text-neutral-700',
              appConfig.isMeater && 'tra-text-web-heading-3',
              appConfig.isTraeger && 'tra-text-web-heading-4',
            )}
          >
            {title}
          </h4>
          {description && (
            <Markdown bodySize="large" className="tw-text-neutral-600">
              {description}
            </Markdown>
          )}
        </div>
        <div className="tw-grid tw-gap-y-10">
          {navigationPromo?.title && (
            <NavigationPromo
              {...navigationPromo}
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          )}
          {centerContent?.map((props, i) => (
            <DynamicComponent
              key={i}
              {...props}
              _componentTypes={componentTypes}
              _field="_meta.schema"
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          ))}
        </div>
        <RightContent
          rightContent={rightContent}
          SocialLinks={SocialLinks}
          searchLink={searchLink}
          primaryHeader={primaryHeader}
          secondaryHeader={secondaryHeader}
        />
      </div>
    </>
  );
};

export default NavDropdown;
